import { graphql } from 'gatsby';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import html from 'remark-html';
import markdown from 'remark-parse';
import unified from 'unified';

import config from '../../config';

// Layout
import Micro from '../../layouts/Micro';

// Components
import SEO from '../../components/SEO/SEO';
import LatestArticles from '../../components/LatestArticles/LatestArticles';
import Carousel from '../../components/Carousel/Carousel';
import LeadMagnet from '../../components/LeadMagnet/LeadMagnet';
import Link from '../../components/Link/Link';
import Image from '../../components/Image/Image';
import PostListItem from '../../components/Post/PostListItem/PostListItem';

// Search Components
import SearchBox from '../../components/Discovery/SearchBox/SearchBox';
import RefinementList from '../../components/Discovery/RefinementList/RefinementList';
import TagsSearchBox from '../../components/Discovery/TagsSearchBox/TagsSearchBox';

const Blog = ({ data }) => {
  const { nodes } = data.wpcontent.posts;

  const { edges } = data.wpcontent.categories;

  const [query, setQuery] = useState();
  // Results
  const [hitsDefault] = useState(nodes);
  const [hits, setHits] = useState(nodes);

  // Refinement List styles
  const [categories] = useState(
    edges
      .map((edge) => ({ title: edge.node.name, slug: edge.node.slug }))
      .sort((a, b) => `${a.name}`.localeCompare(b.name))
  );

  const [isClicked, setisClicked] = useState(false);
  const [activeItem, setActiveItem] = useState();

  // Tags Search
  const [tags] = useState(nodes.map((post) => post.tags));

  // Update input func
  const updateInput = async (input) => {
    const filtered = hitsDefault.filter((hit) =>
      hit.title.toLowerCase().includes(input.toLowerCase())
    );
    setQuery(input);
    setHits(filtered);
  };

  const onSelect = (prevItem, selectedItem) => {
    const prevSelectedItem = prevItem[1];

    const filtered = hitsDefault.filter(
      (hit) =>
        selectedItem &&
        hit.categories.nodes.length &&
        hit.categories.nodes[0].slug &&
        hit.categories.nodes[0].slug.toLowerCase().includes(selectedItem.toLowerCase())
    );

    if (!isClicked) {
      setisClicked(true);
      setActiveItem(selectedItem);
      setHits(filtered);
    } else if (prevSelectedItem !== selectedItem) {
      setisClicked(true);
      setActiveItem(selectedItem);
      setHits(filtered);
    } else {
      setisClicked(false);
      setActiveItem('');
      setHits(hitsDefault);
    }
  };

  const selectedTags = (selectedTag) => {
    const filters = selectedTag?.map((tag) => tag.value);

    if (filters?.length) {
      const filtered = hitsDefault.filter((hit) => filters.includes(hit.tags.nodes[0]?.slug));
      setHits(filtered);
    } else {
      setHits(hitsDefault);
    }
  };

  return (
    <Micro>
      <SEO title="Blog" />
      <div className="container my-2">
        <div className="row">
          <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6">
            <div className="container-title my-2">
              <h1 className="text-bold">
                Blog | <span className="text-muted text-normal">Work From Home Blog</span>
              </h1>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-4 col-xs-6">
            <div className="slider-area">
              <Carousel>
                {nodes.map((node) => (
                  <Link
                    key={node.id + node.uri}
                    href={`/blog${node.uri}`}
                    rel="noopener noreferrer"
                    title={node.title}
                  >
                    <Image
                      img={node?.featuredImage?.node?.mediaItemUrl}
                      alt={node.title}
                      customClass="slider-area-image"
                      height="450px"
                      radius={20}
                    />
                    <div className="slider-area-content my-2 text-left">
                      <h3>{node.title}</h3>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: unified().use(markdown).use(html).processSync(node.excerpt),
                        }}
                      />
                    </div>
                  </Link>
                ))}
              </Carousel>
            </div>
          </div>

          <div className="col-lg-2 col-md-6 col-sm-6 col-xs-6 flex flex-end">
            <LatestArticles posts={nodes} />
          </div>
        </div>
        <div className="row flex-center">
          <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6">
            <LeadMagnet src={config.blogDesktopBanner} mobileSrc={config.blogMobileBanner} />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6">
            <RefinementList
              menuTitle="Categories"
              data={categories}
              onSelect={onSelect}
              activeItem={activeItem}
              isHorizontal
              isVertical={false}
            />
          </div>
        </div>
        <div className="row" style={{ paddingLeft: '.5rem', paddingRight: '.5rem' }}>
          <div className="col-lg-2 col-md-6 col-sm-6 col-xs-6">
            <TagsSearchBox tags={tags} onChange={selectedTags} />
          </div>
          <div className="col-lg-2 col-md-6 col-sm-6 col-xs-6" />

          <div className="col-lg-2 col-md-6 col-sm-6 col-xs-6">
            <SearchBox placeholder="Search blog" query={query} onChange={updateInput} />
          </div>
        </div>

        <div className="row mt-1 mb-3">
          <div className="row">
            {hits.map((hit) => {
              const postData = {
                title: hit.title,
                slug: hit.slug,
                content: hit.content,
                featuredImage: hit?.featuredImage?.node?.mediaItemUrl,
                excerpt: hit.excerpt,
                categoryName: hit?.categories?.nodes[0]?.name,
                categorySlug: hit?.categories?.nodes[0]?.slug,
              };
              return <PostListItem key={hit.slug} post={postData} />;
            })}
          </div>
        </div>
      </div>
    </Micro>
  );
};

Blog.propTypes = {
  data: PropTypes.object.isRequired, // eslint-disable-line
};

export default Blog;

export const pageQuery = graphql`
  {
    wpcontent {
      posts(where: { parent: null, status: PUBLISH }, first: 100) {
        nodes {
          title
          uri
          date
          content
          excerpt
          modified
          slug
          id
          featuredImage {
            node {
              mediaItemUrl
            }
          }
          categories {
            nodes {
              name
              slug
            }
          }
          tags {
            nodes {
              name
              slug
              uri
            }
          }
        }
      }
      categories {
        edges {
          node {
            link
            name
            id
            slug
            uri
          }
        }
      }
    }
  }
`;
